/*
 * Copyright (C) 2018-2019 Nettoken Ltd. All rights reserved.
 *
 * This document is the property of Nettoken Ltd.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Nettoken Ltd.
 */
import KeyPair from './keypair';
import operations from './operations';

let authString = '';

let b64variant;
let externalKeyPair;
let masterKeyPair;
let sessionKeyPair;
let sodium;

let convertInput;
let decryptCredential;
let decryptDashboard;
let decryptSharedDashboard;
let decryptSharedCredential;
let decryptUsingExternalKeyPair;
let decryptUsingMasterKeyPair;
let decryptUsingSessionKeyPair;
let encryptCredential;
let encryptSharedCredential;
let encryptUsingExternalKeyPair;
let encryptUsingSessionKeyPair;
let generateKeyPair;
let setSessionKeyPair;
let encryptUsingMasterKeyPair;

/**
 * @param {*} input
 * @param {*} inputFormat
 * @param {*} outputFormat
 *
 * @returns {object}
 * @property {*} output
 */
const convertInputHandler = async (input, inputFormat, outputFormat) => {
  const output = convertInput(input, inputFormat, outputFormat);
  return { output };
};

/**
 * Decrypt a list of credentials. We exclude credentials where
 * decryption failed.
 *
 * @param {array} credentials Encrypted credential objects.
 *
 * @return {object}
 * @property {array} decrypted Plaintext credential objects.
 */
const decryptCredentials = async credentials => {
  const decrypted = credentials.map(x => {
    if (x.shared) {
      const { secretKey, publicKey } = x;
      const { secretKeyBytes, publicKeyBytes } = extractSharedKey(publicKey, secretKey);
      const tempKeyPair = new KeyPair();
      tempKeyPair.set(publicKeyBytes, secretKeyBytes);
      return decryptSharedCredential(x, tempKeyPair)
    }
    return decryptCredential(x);
  })
  .filter(x => !!x);
  return { decrypted };
};

const decryptDashboards = dashboard => {
  const decrypted = dashboard.map(x => {

    if (x.shared) {
      const { secretKey, publicKey } = x;     
      const { secretKeyBytes, publicKeyBytes } = extractSharedKey(publicKey, secretKey);
      const tempKeyPair = new KeyPair();
      tempKeyPair.set(publicKeyBytes, secretKeyBytes);
      return decryptSharedDashboard(x, tempKeyPair);
    }
    return decryptDashboard(x);
  })
  .filter(x => !!x);
  return { decrypted };
};
/**
 * Encrypt a list of credentials. We exclude credentials where
 * encryption failed.
 *
 * @param {array} credentials Plaintext credential objects.
 *
 * @return {object}
 * @property {array} encrypted Encrypted credential objects.
 */
const encryptCredentials = async credentials => {
  const encrypted = credentials.map(x => {
    if (x.shared) {
      const { secretKey, publicKey } = x;
      const { secretKeyBytes, publicKeyBytes } = extractSharedKey(publicKey, secretKey);
      const tempKeyPair = new KeyPair();
      tempKeyPair.set(publicKeyBytes, secretKeyBytes);
      return encryptSharedCredential(x, tempKeyPair);
    }
    return encryptCredential(x)
  }).filter(x => !!x);
  return { encrypted };
};

/**
 * Decrypt the encrypted secret key sent from the mobile app. This key
 * was encrypted using our (web) public key, so we should be able to
 * decrypt it.
 *
 * @param {string} encryptedSecretKey Base64 encoded encrypted secret key.
 * @param {string} publicKey Base64 encoded public key.
 *
 * @returns {object}
 * @property {boolean} [OK] True if decryption passed.
 */
const decryptMasterSecretKey = async (encryptedSecretKey, publicKey) => {
  try {
    const publicKeyBytes = sodium.from_base64(publicKey, b64variant);
    const secretKeyBytes = decryptUsingSessionKeyPair(encryptedSecretKey, 'bytes');
    masterKeyPair.set(publicKeyBytes, secretKeyBytes);
    return { OK: true };
  }
  catch (e) {
    console.log(e);
    return {};
  }
};

/**
 * Decrypt a secret message that was previously encrypted using our external
 * key pair.
 *
 * @param {string} encryptedMessage Base64 encrypted secret message
 * that can be decrypted using our external key pair.
 * @param {string} [format='plain'] Output message format.
 * @param {string} [publicKeyBase64] Base64 encoded public key from external key pair.
 * @param {string} [secretKeyBase64] Base64 encoded secret key from external key pair.
 *
 * @returns {object}
 * @property {*} message
 */
const decryptWithExternalKeys = async (encryptedMessage, format = 'plain', publicKeyBase64, secretKeyBase64) => {
  let tempKeyPair = externalKeyPair;
  if (publicKeyBase64 && secretKeyBase64) {
    tempKeyPair = new KeyPair();
    const tempPublicKeyBytes = sodium.from_base64(publicKeyBase64, b64variant);
    const tempSecretKeyBytes = sodium.from_base64(secretKeyBase64, b64variant);
    tempKeyPair.set(tempPublicKeyBytes, tempSecretKeyBytes);
  }
  const message = decryptUsingExternalKeyPair(encryptedMessage, format, tempKeyPair);
  return { message };
};

/**
 * Returns a public key derived from the passed secret key. This value will
 * be always constant for the given secret key.
 *
 * @param {string} secretKeyBase64 Base64 encoded secret key.
 *
 * @property {object}
 * @property {string} publicKey
 */
const derivePublicKeyFromSecret = async secretKeyBase64 => {
  const secretKey = sodium.from_base64(secretKeyBase64, b64variant);
  const publicKey = sodium.crypto_scalarmult_base(secretKey);
  return {
    publicKey: sodium.to_base64(publicKey, b64variant),
  };
};

/**
 * Encrypt the mobile app secret key. This is a mock function and should be
 * used only in testing or debug modes.
 *
 * @param {string} secretKey Base64 encoded secret key to be encrypted.
 *
 * @return {object}
 * @property {string} [encrypted] Base64 encoded encrypted secret key.
 */
const encryptMasterSecretKey = async secretKey => {
  try {
    const encrypted = encryptUsingSessionKeyPair(secretKey);
    return { encrypted };
  }
  catch (e) {
    console.log(e);
    return {};
  }
};

/**
 * Encrypt a random message using a random supplied public key. This
 * method is used if we want to export some value from current environment
 * to another environment, so the public key must come from the target.
 * Returns encrypted message in base64 string.
 *
 * @param {string} message Secret message to encrypt.
 * @param {string} externalPublicKey Base64 public key from an external
 *   key pair that does not belong to this environment.
 * @param {string} [inputFormat='plain'] Input message format.
 * @param {string} [outputFormat='base64'] Output message format.
 *
 * @returns {object}
 * @property {*} [encrypted] Encrypted message.
 */
const encryptWithExternalKey = async (message, externalPublicKey, inputFormat = 'plain', outputFormat = 'base64') => {
  try {
    // Use public key for both values otherwise KeyPair
    // would complain about undefined secret key.
    const tempKeyPair = new KeyPair();
    const tempPublicKeyBytes = sodium.from_base64(externalPublicKey, b64variant);
    tempKeyPair.set(tempPublicKeyBytes, tempPublicKeyBytes);
    const encrypted = encryptUsingExternalKeyPair(message, tempKeyPair, inputFormat, outputFormat);
    return { encrypted };
  }
  catch (e) {
    console.log(e);
    return {};
  }
};

/**
 * Extract the master key pair. We do this when we want to pass its value
 * from web app to extension or vice versa. We would pass this value around
 * when one client is already authenticated and we want to automatically
 * authenticate the other one without hitting the servers. We encrypt the
 * secret key before returning it to match the value we would get from the
 * server. It would be also dangerous to pass it around in plaintext
 *
 * @param {*} [tempPublicKey] Bytes array for public key from external key pair.
 * @param {*} [tempSecretKey] Bytes array for secret key from external key pair.
 *
 * @returns {object}
 * @property {*} [encryptedPrivateKey]
 * @property {*} [publicKey]
 */
const extractMasterKeys = async (tempPublicKey, tempSecretKey) => {
  try {
    const publicKey = sodium.to_base64(masterKeyPair.publicKey, b64variant);
    const secretKeyBase64 = sodium.to_base64(masterKeyPair.secretKey, b64variant);
    let encryptedPrivateKey = '';

    if (tempPublicKey && tempSecretKey) {
      const tempKeyPair = new KeyPair();
      tempKeyPair.set(tempPublicKey, tempSecretKey);
      encryptedPrivateKey = encryptUsingExternalKeyPair(secretKeyBase64, tempKeyPair);
    }
    else {
      encryptedPrivateKey = encryptUsingSessionKeyPair(secretKeyBase64);
    }

    return { encryptedPrivateKey, publicKey };
  }
  catch (e) {
    console.log(e);
    return {};
  }
};

/**
 * Extract the current session key pair. We do this when we want to pass its
 * value from web app to extension or vice versa. As this is a potentially
 * vulnerable process, we encrypt these keys before returning them. This way,
 * we reduce the change of someone accidentally extracting keys and not
 * securing them properly.
 *
 * @param {string} tempPublicKey Base64 public key to use to encrypt the values.
 *
 * @returns {object}
 * @property {*} [publicKey]
 * @property {*} [secretKey]
 */
const extractSessionKeys = async tempPublicKey => {
  try {
    const publicKeyBase64 = sodium.to_base64(sessionKeyPair.publicKey, b64variant);
    const secretKeyBase64 = sodium.to_base64(sessionKeyPair.secretKey, b64variant);

    // Use public key for both values otherwise it would be undefined.
    const tempKeyPair = new KeyPair();
    const tempPublicKeyBytes = sodium.from_base64(tempPublicKey, b64variant);
    tempKeyPair.set(tempPublicKeyBytes, tempPublicKeyBytes);

    const publicKey = encryptUsingExternalKeyPair(publicKeyBase64, tempKeyPair);
    const secretKey = encryptUsingExternalKeyPair(secretKeyBase64, tempKeyPair);
    return { publicKey, secretKey };
  }
  catch (e) {
    // Fail silently.
    return {};
  }
};

const extractSharedKey = (tempPublicKey, tempSecretKey) => {
  const publicKeyPlain = decryptUsingMasterKeyPair(tempPublicKey, 'plain');
  const publicKeyBytes = sodium.from_base64(publicKeyPlain, b64variant);
  const secretKeyPlain = decryptUsingMasterKeyPair(tempSecretKey, 'plain');
  const secretKeyBytes = sodium.from_base64(secretKeyPlain, b64variant);
  return { secretKeyBytes, publicKeyBytes };
}

/**
 * Return a new key pair. This is used to get a key pair value outside this
 * module, for example for encrypting custom data not supported by
 * this package.
 *
 * @param {string} format
 *
 * @returns {object}
 * @property {*} publicKey
 * @property {*} secretKey
 */
const generateEphemeralKeyPair = async format => {
  const { publicKey, secretKey } = generateKeyPair(false, format);
  return { publicKey, secretKey };
};

/**
 * Return an authentication string to be embedded into a QR code.
 *
 * @returns {object}
 * @property {string} publicKey Defaults to empty string.
 * @property {string} socketId Defaults to empty string.
 */
const getAuthString = async () => {
  const [socketId, publicKey] = authString.split(',');
  return {
    publicKey: publicKey || '',
    socketId: socketId || '',
  };
};

/**
 * Returns the external key pair public key. If the key pair was not defined,
 * it gets defined here.
 *
 * @returns {object}
 * @property {string} publicKey Base64 encoded public key.
 */
const getExternalPublicKey = async () => {
  if (!externalKeyPair.defined()) {
    const { publicKey, privateKey: secretKey } = sodium.crypto_box_keypair();
    externalKeyPair.set(publicKey, secretKey);
  }

  let { publicKey } = externalKeyPair;
  publicKey = sodium.to_base64(publicKey, b64variant);
  return { publicKey };
};

/**
 * Resets the authentication string. Used in testing.
 *
 * @returns {object}
 */
const resetAuthString = async () => {
  authString = '';
  return getAuthString();
};

/**
 * Resets all key pairs. Used in testing.
 *
 * @returns {object}
 */
const resetKeys = async () => {
  masterKeyPair.unset();
  sessionKeyPair.unset();
  return {};
};

/**
 * Sets a new session key pair on every run to generate a unique authentication
 * string.
 *
 * @param {string} socketId WebSocket socket id.
 * @param {boolean} mock Use mock key pair?
 *
 * @returns {object}
 */
const setAuthString = async (socketId, mock) => {
  setSessionKeyPair(mock);
  const publicKey = sodium.to_base64(sessionKeyPair.publicKey, b64variant);
  authString = `${socketId || ''},${publicKey}`;
  return getAuthString();
};

/**
 * Replaces session key pair with external values. These values are encrypted
 * using our external key pair which we presumably generated in the previous
 * step.
 *
 * @param {string} publicKey Encrypted session public key.
 * @param {string} secretKey Encrypted session secret key.
 */
const setSessionKeys = async (publicKey, secretKey) => {
  try {
    publicKey = decryptUsingExternalKeyPair(publicKey, 'bytes');
    secretKey = decryptUsingExternalKeyPair(secretKey, 'bytes');
    sessionKeyPair.set(publicKey, secretKey);
  }
  catch (e) {
    console.log(e);
  }
};

const encryptUsingMasterKeyPairHandler = (message, inputFormat, outputFormat) => {
  return encryptUsingMasterKeyPair(message, inputFormat, outputFormat);
}

const decryptUsingMasterKeyPairHandler = (message, inputFormat, outputFormat) => {
  return decryptUsingMasterKeyPair(message, inputFormat, outputFormat);
}

const exported = {
  convertInput: convertInputHandler,
  decryptCredentials,
  decryptDashboards,
  decryptMasterSecretKey,
  decryptWithExternalKeys,
  derivePublicKeyFromSecret,
  encryptCredentials,
  encryptMasterSecretKey,
  encryptWithExternalKey,
  extractMasterKeys,
  extractSessionKeys,
  generateEphemeralKeyPair,
  getAuthString,
  getExternalPublicKey,
  resetAuthString,
  resetKeys,
  setAuthString,
  setSessionKeys,
  encryptUsingMasterKeyPair: encryptUsingMasterKeyPairHandler,
  decryptUsingMasterKeyPair: decryptUsingMasterKeyPairHandler,
};

export default (sod, b64v, masterKeys, sessionKeys, externalKeys) => {
  sodium = sod;
  b64variant = b64v;
  externalKeyPair = externalKeys;
  masterKeyPair = masterKeys;
  sessionKeyPair = sessionKeys;

  ({
    convertInput,
    decryptCredential,
    decryptDashboard,
    decryptSharedDashboard,
    decryptSharedCredential,
    decryptUsingExternalKeyPair,
    decryptUsingMasterKeyPair,
    decryptUsingSessionKeyPair,
    encryptCredential,
    encryptSharedCredential,
    encryptUsingExternalKeyPair,
    encryptUsingSessionKeyPair,
    generateKeyPair,
    setSessionKeyPair,
    encryptUsingMasterKeyPair,
  } = operations(sodium, b64variant, masterKeyPair, sessionKeyPair, externalKeyPair));

  return exported;
};
